import React, { useEffect, useMemo, useState, Suspense, lazy } from "react";
import { studyModeLabelOf } from "../studentDisplay";
import { studentEnrollmentTypeByValue } from "../../../Core/constants/studentEnrollmentTypes";
import { studentStatusChipPalette, normalizeStudentStatus, hasPaymentDue } from "../../../Core/constants/studentStatus";
import { DropStudentDialog, ReactivateStudentDialog } from "./Components/StudentLifecycleDialogs";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Grid,
  Typography,
  useTheme,
  Divider,
  DialogContent,
  Rating,
  Chip,
  Stack,
  DialogActions,
  alpha,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ProfileTimelineSection from "../../../Components/CommunicationTimeline/ProfileTimelineSection";
import { useStyles } from "../../../theme/appStyles";
import {
  useDeleteStudentMutation,
  useGetallLocationQuery,
  useGetallGradeQuery,
  useGetallStudentQuery,
  useCreateStudentImageMutation,
  useGetStaffGradesAssignStudentQuery,
  useGetStudentHealthNotesQuery,
} from "../../../Api/attoDeskApi";
import {
  IEnumType,
  IStudent,
  IGrade,
  ISubjectGradeStaff,
  IStudentLocation,
} from "../../../Api/Interface/api.interface";
import CloseIcon from "@mui/icons-material/Close";
import StudentAvatar from "../../../Components/StudentAvatar";
import { formatPhoneNumberForDisplay } from "../../../Components/Validation/validationUtils";
import useUser from "../../../Core/hook/useUser";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { useNotifier } from "../../../Core/Notifier";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { toAllCaps } from "../../../Components/Validation/toAllCaps";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PaymentIcon from "@mui/icons-material/Payment";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useFormik } from "formik";
import { uploadImage } from "../../../Components/UploadFile/upload";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import BackDrop from "../../../Components/Loader/BackDrop";
import { Loader } from "../../../Components/Loader";
import { useStudentPerformanceSummary } from "./hooks/useStudentPerformanceSummary";
import {
  shouldFireStudentQuery,
  readStudentFromResponse,
  decideRender,
} from "./deepLinkLoader";

// Lazy-load every tab section so only the active tab's bundle is fetched.
// Each of these pulls in its own charts / tables / editors — importing them all
// eagerly was the main reason the page felt heavy on first paint.
const ProfileSection = lazy(() => import("./Components/ProfileSection"));
const EnrolledSubjectsSection = lazy(() => import("./Components/EnrolledSubjectsSection"));
const WeeklyScheduleSection = lazy(() => import("./Components/WeeklyScheduleSection"));
const StudentSummary = lazy(() => import("../Component/Summary"));
const MessageSection = lazy(() => import("./Components/Emailtemplete"));
const AssignmentSection = lazy(() => import("./Components/AssignmentSection"));
const PaymentSection = lazy(() => import("./Components/PaymentSection"));
const PerformanceSection = lazy(() => import("./Components/PerformanceSection"));
const ChallengesSection = lazy(() => import("./Components/ChallengesSection"));
const HistorySection = lazy(() => import("./Components/HistorySection"));
const PendingMakeupsSection = lazy(() => import("./Components/PendingMakeupsSection"));
const AssessmentManagement = lazy(() => import("./Components/AssessmentManagement"));

enum Section {
  Summary = "Summary",
  Profile = "Profile",
  EnrolledSubjects = "EnrolledSubjects",
  WeeklySchedule = "WeeklySchedule",
  Message = "Message",
  Challenges = "Challenges",
  Assignment = "Assignment",
  Assessment = "Assessment",
  Payments = "Payments",
  Strengths = "Strengths",
  History = "History",
  PendingMakeups = "PendingMakeups",
}

// The standalone "Calendar" tab was merged into the Dashboard. Old deep-links
// (?tab=Calender / ?tab=Calendar) redirect to the Dashboard and scroll to the
// embedded calendar section, preserving backward compatibility for saved links.
const LEGACY_CALENDAR_TABS = ["Calender", "Calendar"];

const TAB_CONFIG = [
  { key: Section.Summary, label: "Dashboard", icon: <DashboardOutlinedIcon sx={{ fontSize: 18 }} /> },
  { key: Section.Profile, label: "Profile", icon: <PersonOutlineIcon sx={{ fontSize: 18 }} /> },
  { key: Section.EnrolledSubjects, label: "Enrolled Subjects", icon: <MenuBookRoundedIcon sx={{ fontSize: 18 }} /> },
  { key: Section.WeeklySchedule, label: "Weekly Schedule", icon: <CalendarViewWeekIcon sx={{ fontSize: 18 }} /> },
  { key: Section.PendingMakeups, label: "Pending Makeups", icon: <HistoryEduOutlinedIcon sx={{ fontSize: 18 }} /> },
  { key: Section.History, label: "History", icon: <HistoryEduOutlinedIcon sx={{ fontSize: 18 }} /> },
  { key: Section.Message, label: "Messages", icon: <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} /> },
  { key: Section.Challenges, label: "Challenges", icon: <FlagOutlinedIcon sx={{ fontSize: 18 }} /> },
  { key: Section.Assignment, label: "Assignments", icon: <AssignmentOutlinedIcon sx={{ fontSize: 18 }} /> },
  { key: Section.Assessment, label: "Assessments", icon: <AssessmentOutlinedIcon sx={{ fontSize: 18 }} /> },
  { key: Section.Payments, label: "Payments", icon: <PaymentIcon sx={{ fontSize: 18 }} /> },
  { key: Section.Strengths, label: "Strengths & Needs", icon: <TrendingUpIcon sx={{ fontSize: 18 }} /> },
];

const STAT_CARDS = [
  { key: "performance", label: "Performance", color: "#6366f1", bg: "#eef2ff", icon: <BarChartRoundedIcon /> },
  { key: "attendance", label: "Attendance", color: "#0ea5e9", bg: "#f0f9ff", icon: <EventAvailableIcon /> },
  { key: "subjects", label: "Subjects", color: "#f59e0b", bg: "#fffbeb", icon: <MenuBookRoundedIcon /> },
  { key: "payments", label: "Due Payments", color: "#22c55e", bg: "#f0fdf4", icon: <AttachMoneyRoundedIcon /> },
];

const Index = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const tabParam = new URLSearchParams(location.search).get("tab");
  const isLegacyCalendar = !!tabParam && LEGACY_CALENDAR_TABS.includes(tabParam);
  const initialSection =
    tabParam && !isLegacyCalendar && Object.values(Section).includes(tabParam as Section)
      ? (tabParam as Section)
      : Section.Summary;
  const [activeSection, setActiveSection] = useState<Section>(initialSection);
  // Keep the active tab in the URL (?tab=…) so a refresh, a bookmark, or a
  // shared link lands back on the same tab instead of resetting to Dashboard.
  const handleContainerChange = (section: Section) => {
    setActiveSection(section);
    const params = new URLSearchParams(location.search);
    params.set("tab", section);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  // Legacy ?tab=Calendar deep-links land on the Dashboard; once the embedded
  // calendar has actually mounted, scroll to it. The dashboard only renders after
  // the student data resolves, so we poll for the anchor (up to ~10s) instead of
  // a single fixed delay that could fire before the section exists.
  useEffect(() => {
    if (!isLegacyCalendar) return;
    let tries = 0;
    const iv = setInterval(() => {
      const el = document.getElementById("student-calendar-section");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        clearInterval(iv);
      } else if (++tries > 50) {
        clearInterval(iv);
      }
    }, 200);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const theme = useTheme();
  const isMobileRes = useIsMobile();
  const { showErrorMessage, showMessage } = useNotifier();
  const { id: studentId } = useParams<{ id: string }>();
  const { userHasRight } = useUser();

  // Student Actions dropdown + lifecycle dialogs.
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [dropDialogOpen, setDropDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [selectedEmpImg, setSelectedEmpImg] = useState<string>("");
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IStudent | null>(null);
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const canViewStudentNotes = userHasRight("StudentNote_View") as boolean;

  const [newCompany, { isLoading: saveIsLoading }] = useCreateStudentImageMutation();

  // Fetch is driven SOLELY by the URL param — no Redux `selectedStudent`,
  // no `location.state.student`, no dependency on a previously-clicked row.
  // Deep-link (paste `/student-history/13263` in a fresh tab) and refresh
  // must both hit `/student/getAll` with `id=13263` and render the profile.
  //
  // `skip` guards against firing with `id="undefined"` — an unparsable
  // route param would previously reach the backend as the literal string
  // "undefined" (truthy in `->when($id, ...)`) and match zero rows, which
  // in turn rendered "No student data available." for what is actually a
  // routing precondition failure.
  const hasStudentId = shouldFireStudentQuery(studentId);
  const { data, isLoading, isFetching, isError } = useGetallStudentQuery(
    { searchText: "", id: String(studentId) },
    { skip: !hasStudentId },
  );

  const { data: locationData, isLoading: locationIsLoading } =
    useGetallLocationQuery({ searchText: "" });

  const { data: gradeData, isLoading: gradeIsLoading } = useGetallGradeQuery({
    searchText: "",
  });

  const {
    data: subjectGradeData,
    isLoading: subjectGradeIsLoading,
    refetch: subjectGradeRefetch,
  } = useGetStaffGradesAssignStudentQuery({ id: String(studentId) });

  const subjectCount = useMemo(() => {
    const list = Array.isArray(subjectGradeData?.data)
      ? (subjectGradeData!.data as ISubjectGradeStaff[])
      : [];
    return list.reduce((acc, sg) => acc + (sg.subject_ids?.length || 0), 0);
  }, [subjectGradeData]);

  // Two additional summary fetches feed the stat strip (performance +
  // payments). RTK Query dedupes by cache key so PerformanceSection and
  // PaymentSection reuse these same cache entries — no duplicate network
  // work, and the stat card and tab section physically cannot show
  // different numbers. Cost of two extra queries on page load is the
  // deliberate trade for real figures on the primary tab.
  const perfSummary = useStudentPerformanceSummary(String(studentId));

  // Currency formatter matches PaymentSection's `$${Number(x).toFixed(2)}`
  // at line 263 / 406 / 471 so the stat card and Payments tab render the
  // outstanding amount identically.
  const formatDue = (v: number | null): string =>
    v == null ? "—" : `$${Number(v).toFixed(2)}`;
  const formatPct = (v: number | null): string => (v == null ? "—" : `${v}%`);

  // During load, render "—" rather than a zero. A card that flashes
  // "$0.00" before "$340.00" is worse than one that stays blank.
  const statValues: Record<string, string> = {
    performance: perfSummary.isLoading ? "—" : formatPct(perfSummary.performancePct),
    attendance: perfSummary.isLoading ? "—" : formatPct(perfSummary.attendancePct),
    subjects: String(subjectCount),
    payments: perfSummary.isLoading ? "—" : formatDue(perfSummary.outstandingDue),
  };

  const gradeList = useMemo(() => {
    return gradeData?.data as IGrade[];
  }, [gradeData?.data]);

  const locationList = useMemo(() => {
    return (locationData?.data ?? []) as IStudentLocation[];
  }, [locationData?.data]);

  const gradeMap = useMemo(() => {
    const map = new Map();
    gradeList?.forEach((category) => {
      map.set(category.id, category.grade);
    });
    return map;
  }, [gradeList]);

  // Response-shape guard: /student/getAll returns `{ data: [] }` on the
  // non-paginated path (see studentsController::index, line ~1146). Read
  // that array and pull the first (only) row when an `id` filter is set.
  // Defensive fallback: if a future call site ever ships a single-object
  // shape (e.g. `{ data: {...} }`), pick it up too so this page stops
  // dead only when the row genuinely does not exist.
  const studentData = useMemo(
    () => (readStudentFromResponse(data) as IStudent | null),
    [data],
  );

  const locationMap = useMemo(() => {
    const map = new Map();
    locationList?.forEach((category) => {
      map.set(category.id, category.location_name);
    });
    return map;
  }, [locationList]);

  const handleOpenImage = (imgUrl: string) => {
    setSelectedEmpImg(imgUrl);
    setOpenImage(true);
  };

  const handleCloseImage = () => {
    setSelectedEmpImg("");
    setOpenImage(false);
  };

  const formik = useFormik({
    initialValues: {
      id: String(studentId) || "",
      image: studentData?.image || "",
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        let image: string | undefined = studentData?.image;

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("image", selectedImage);
          image = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        const updatedData = { id: Number(values.id), image };
        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleCloseImage();
          setSelectedImage(undefined);
        }
      } catch (error: any) {
        console.error("Operation failed:", error);
        showErrorMessage(error?.data?.message || error?.message || "Something went wrong");
      }
    },
  });

  const formikRemoved = useFormik({
    initialValues: {
      id: String(studentId) || "",
      image: "",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const updatedData = { id: Number(values.id), image: values.image };
        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleCloseImage();
          setSelectedImage(undefined);
        }
      } catch (error: any) {
        console.error("Operation failed:", error);
        showErrorMessage(error?.data?.message || error?.message || "Something went wrong");
      }
    },
  });

  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
          } else {
            if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
          }
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: file.type }));
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };
        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024;
      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const handleOpenDeletePopup = (coursing: IStudent) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteStudentMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage("Deleted successfully");
        setOpenDeleteCategory(false);
        navigate(`/student/list`);
      } else {
        showErrorMessage("Failed to delete the staff");
      }
    } catch (error) {
      showErrorMessage("Failed to delete the staff");
    }
  };

  // Only block the initial paint on the student query itself. Grade/location
  // lookups just decorate a couple of chips — let them stream in after the
  // hero card is on screen instead of holding the whole page behind a spinner.
  //
  // Guard against two false-empty paths that previously rendered
  // "No student data available." for a valid student:
  //  (1) The route param hasn't resolved yet (`!hasStudentId`) — happens
  //      transiently on a direct deep-link mount before React Router
  //      settles params on the first render pass.
  //  (2) The query is refetching (`isFetching`) — `isLoading` only covers
  //      the very first request; a navigation from one student profile
  //      to another (or a tab-driven param change) leaves `isLoading`
  //      false while `data` briefly reads the new cache key as
  //      undefined, and the naked `!studentData` branch below would
  //      flash the empty state.
  const renderDecision = decideRender({
    hasStudentId,
    isLoading,
    isFetching,
    isError,
    studentResolved: !!studentData,
  });
  if (renderDecision === "loading") {
    return <Loader />;
  }
  if (renderDecision === "error") {
    return (
      <Typography color="error">
        Error fetching Student data. Please try again later.
      </Typography>
    );
  }
  // Only surfaced when the API resolved cleanly with no matching row —
  // i.e. the student genuinely does not exist / is not visible to the
  // caller. Never shown mid-fetch; see `decideRender`'s loading branch.
  // The `!studentData` check also narrows the type below to non-null.
  if (renderDecision === "empty" || !studentData) {
    return <Typography>No student data available.</Typography>;
  }

  const studentFullName = [
    studentData.first_name,
    studentData.middle_name,
    studentData.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const gradeLabel = studentData.grade_id
    ? gradeMap.get(studentData.grade_id) || ""
    : "";
  const locationLabel = studentData.location_id
    ? locationMap.get(studentData.location_id) || ""
    : "";

  // Resolved parent for the compact contact row under the student name.
  //
  // The header used to read `studentData.parent` directly, which is the legacy
  // `students.parent_id → student_parents` belongsTo relation. On students
  // whose parent link lives ONLY on the modern `relationship_tables` pivot
  // (e.g. TEST STUDENT id=13263 — `students.parent_id` is NULL but a linked
  // parent exists via relationship_tables), that direct relation returns null
  // and the header silently omitted the parent name / phone / email row even
  // though the contact was actually available on the same payload.
  //
  // Fall back through:
  //   1. studentData.parent           — legacy FK, still authoritative when set
  //   2. relationships.find(isPrimary) — modern pivot, preferred parent
  //   3. relationships[0]              — any linked parent (never render blank)
  //
  // Only fields the render already consumes are surfaced; the shape matches
  // the `student_parents` row so the JSX below can stay unchanged.
  const resolvedParent: any = (() => {
    const legacy = (studentData as any).parent;
    if (legacy && (legacy.contact_number || legacy.email)) return legacy;
    const rels: any[] = Array.isArray((studentData as any).relationships)
      ? (studentData as any).relationships
      : [];
    const primary = rels.find((r) => r?.isPrimary && r?.parent);
    const anyRel  = rels.find((r) => r?.parent);
    return (primary?.parent ?? anyRel?.parent) || null;
  })();

  const renderContent = () => {
    switch (activeSection) {
      case Section.Profile:
        return <ProfileSection staffGetId={String(studentId)} />;
      case Section.EnrolledSubjects:
        return (
          <EnrolledSubjectsSection
            studentData={studentData}
            staffGetId={String(studentId)}
          />
        );
      case Section.WeeklySchedule:
        return (
          <WeeklyScheduleSection
            studentId={String(studentId)}
            onBackToDashboard={() => handleContainerChange(Section.Summary)}
          />
        );
      case Section.History:
        return <HistorySection studentId={String(studentId)} />;
      case Section.PendingMakeups:
        return <PendingMakeupsSection studentId={String(studentId)} />;
      case Section.Summary:
        return (
          <StudentSummary
            studentData={studentData}
            staffGetId={String(studentId)}
            onViewHistory={() => handleContainerChange(Section.History)}
          />
        );
      case Section.Message:
        return <MessageSection staffGetId={String(studentId)} />;
      case Section.Assignment:
        return (
          <AssignmentSection
            studentData={studentData}
            staffGetId={String(studentId)}
          />
        );
      case Section.Payments:
        return (
          <PaymentSection
            studentData={studentData}
            staffGetId={String(studentId)}
          />
        );
      case Section.Strengths:
        return (
          <PerformanceSection
            studentData={studentData}
            staffGetId={String(studentId)}
          />
        );
      case Section.Challenges:
        return (
          <ChallengesSection
            studentData={studentData}
            staffGetId={String(studentId)}
          />
        );
      case Section.Assessment:
        return (
          <AssessmentManagement
            studentData={studentData}
            staffGetId={String(studentId)}
          />
        );
      default:
        return null;
    }
  };

  return (
    // Full-bleed background wrapper. The negative horizontal margin must match the
    // app <main> padding (px: { xs:1.5, sm:2, md:3 }) at EACH breakpoint, otherwise
    // a fixed -24px over-bleeds past <main>'s smaller tablet padding and pushes the
    // page wider than the viewport.
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", mx: { xs: -1.5, sm: -2, md: -3 }, mt: -3, p: 3 }}>
      {/* ════════════════════════════════════════════
          PROFILE HERO CARD
         ════════════════════════════════════════════ */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #0d1b2a 0%, #1b263b 60%, #243447 100%)",
          borderRadius: "16px",
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "40%",
            height: "100%",
            background: "radial-gradient(ellipse at top right, rgba(59,130,246,0.15), transparent 70%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2.5,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Left: Avatar + Info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            <StudentAvatar
              student={studentData}
              fallbackLabel={studentFullName}
              size={68}
              onClick={() => handleOpenImage(studentData.image)}
              sx={{
                border: "3px solid rgba(255,255,255,0.2)",
                cursor: "pointer",
                fontSize: 24,
                transition: "transform 0.2s",
                "&:hover": { transform: "scale(1.05)" },
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: 20, md: 24 },
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                {toAllCaps(studentData.first_name)}{" "}
                {studentData.middle_name ? toAllCaps(studentData.middle_name) + " " : ""}
                {toAllCaps(studentData.last_name)}
              </Typography>

              {/* Status + Payment-Due row — Active / Dropped chip plus a
                  red-flag pill when outstanding_balance > 0. Both are
                  driven purely by backend fields (students.status
                  normalised via normalizeStudentStatus, and
                  payment_due / outstanding_balance from the
                  studentsController::index enrichment). */}
              {(() => {
                const statusPalette = studentStatusChipPalette((studentData as any).status);
                const isDropped = normalizeStudentStatus((studentData as any).status) === "dropped";
                const dropSinceISO = (studentData as any).drop_start_date;
                const paymentDueFlag = hasPaymentDue(studentData as any);
                const outstanding = Number((studentData as any).outstanding_balance ?? 0);
                const statusLabel = isDropped && dropSinceISO
                  ? `${statusPalette.label} · Since ${new Date(dropSinceISO).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                  : statusPalette.label;
                return (
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.75} flexWrap="wrap">
                    <Chip
                      label={statusLabel}
                      size="small"
                      sx={{
                        bgcolor: alpha(statusPalette.chipBgHex, 0.25),
                        color: statusPalette.chipFgHex,
                        border: `1px solid ${alpha(statusPalette.chipBgHex, 0.4)}`,
                        fontSize: 11,
                        fontWeight: 700,
                        height: 24,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                    {paymentDueFlag && (
                      <Tooltip title={outstanding > 0 ? `Outstanding balance: $${outstanding.toFixed(2)}` : "Outstanding balance"}>
                        <Chip
                          icon={<FlagOutlinedIcon sx={{ fontSize: "13px !important", color: "#fecaca !important" }} />}
                          label="Payment Due"
                          size="small"
                          sx={{
                            bgcolor: alpha("#ef4444", 0.25),
                            color: "#fecaca",
                            border: "1px solid rgba(239,68,68,0.4)",
                            fontSize: 11,
                            fontWeight: 700,
                            height: 24,
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </Tooltip>
                    )}
                    {/* Health Alert — driven by studentsController::index enrichment
                        (health_alert.has_alert). Server only sets this field when
                        the caller holds StudentNote_View, so we do not need to
                        re-check the permission here to hide the badge. */}
                    {(studentData as any)?.health_alert?.has_alert && (() => {
                      const count = Number((studentData as any).health_alert.count || 0);
                      return (
                        <Tooltip title="Open health & safety notes">
                          <Chip
                            icon={<MedicalServicesOutlinedIcon sx={{ fontSize: "13px !important", color: "#fde68a !important" }} />}
                            label={count > 1 ? `Health Alert · ${count}` : "Health Alert"}
                            size="small"
                            onClick={() => setHealthDialogOpen(true)}
                            sx={{
                              bgcolor: alpha("#f59e0b", 0.28),
                              color: "#fde68a",
                              border: "1px solid rgba(245,158,11,0.5)",
                              fontSize: 11,
                              fontWeight: 700,
                              height: 24,
                              cursor: "pointer",
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </Stack>
                );
              })()}

              <Stack direction="row" spacing={1} alignItems="center" mt={0.75} flexWrap="wrap">
                {gradeLabel && (
                  <Chip
                    icon={<SchoolOutlinedIcon sx={{ fontSize: "13px !important", color: "rgba(255,255,255,0.7) !important" }} />}
                    label={gradeLabel}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.1)",
                      color: "rgba(255,255,255,0.9)",
                      fontSize: 11.5,
                      fontWeight: 500,
                      height: 24,
                      border: "1px solid rgba(255,255,255,0.1)",
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
                {locationLabel && (
                  <Chip
                    icon={<LocationOnOutlinedIcon sx={{ fontSize: "13px !important", color: "rgba(255,255,255,0.7) !important" }} />}
                    label={locationLabel}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.1)",
                      color: "rgba(255,255,255,0.9)",
                      fontSize: 11.5,
                      fontWeight: 500,
                      height: 24,
                      border: "1px solid rgba(255,255,255,0.1)",
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
                {(studentData as any).program_label && (() => {
                  // Palette lookup from the central enrollment-type
                  // constant so Regular / ABA / OT / Speech chips
                  // stay consistent everywhere the label surfaces.
                  const enroll = studentEnrollmentTypeByValue((studentData as any).program_type);
                  const bg = enroll?.chipTintHex ?? "#22c55e";
                  const fg = enroll?.chipTextHex ?? "#bbf7d0";
                  return (
                    <Chip
                      label={(studentData as any).program_label}
                      size="small"
                      sx={{
                        bgcolor: alpha(bg, 0.25),
                        color: fg,
                        fontSize: 11,
                        fontWeight: 600,
                        height: 24,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  );
                })()}
                {studentData.study_mode && (
                  <Chip
                    label={studyModeLabelOf(studentData.study_mode)}
                    size="small"
                    sx={{
                      bgcolor: alpha("#3b82f6", 0.25),
                      color: "#93c5fd",
                      fontSize: 11,
                      fontWeight: 600,
                      height: 24,
                      textTransform: "capitalize",
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
              </Stack>

              <Stack direction="row" spacing={2.5} alignItems="center" mt={1} flexWrap="wrap">
                {resolvedParent?.contact_number && (() => {
                  const parentName = [resolvedParent?.first_name, resolvedParent?.middle_name, resolvedParent?.last_name]
                    .filter(Boolean).join(" ").trim();
                  return (
                    <Tooltip title={parentName ? `Primary Parent: ${parentName}` : "Primary Parent"}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.45)" }} />
                        <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.7)", fontWeight: 400 }}>
                          <Box component="span" sx={{ color: "rgba(255,255,255,0.5)", mr: 0.5 }}>Primary Parent:</Box>
                          {formatPhoneNumberForDisplay(resolvedParent.contact_number)}
                          {parentName && (
                            <Box component="span" sx={{ color: "rgba(255,255,255,0.5)", ml: 0.75 }}>· {parentName}</Box>
                          )}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })()}
                {resolvedParent?.email && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <EmailIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.45)" }} />
                    <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.7)", fontWeight: 400 }}>
                      {resolvedParent.email}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>

          {/* Right: Action Buttons */}
          <Stack direction="row" spacing={1.25} flexWrap="wrap">
            <Button
              variant="contained"
              size="medium"
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleContainerChange(Section.Profile)}
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: "10px",
                px: 2.5,
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.2)",
                  boxShadow: "none",
                },
              }}
            >
              Edit Profile
            </Button>
            <Button
              variant="contained"
              size="medium"
              startIcon={<PaymentIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleContainerChange(Section.Payments)}
              sx={{
                bgcolor: "rgba(255,255,255,0.12)",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: "10px",
                px: 2.5,
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.2)",
                  boxShadow: "none",
                },
              }}
            >
              Payments
            </Button>
            <Button
              variant="contained"
              size="medium"
              startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
              onClick={() => handleContainerChange(Section.Message)}
              sx={{
                bgcolor: "#3b82f6",
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: "10px",
                px: 2.5,
                boxShadow: "0 2px 8px rgba(59,130,246,0.3)",
                "&:hover": {
                  bgcolor: "#2563eb",
                  boxShadow: "0 4px 12px rgba(59,130,246,0.4)",
                },
              }}
            >
              Message
            </Button>
            {/* Student Actions dropdown — Drop / Reactivate. The two
                actions are mutually exclusive: only the one relevant
                for the current status is enabled. Gated at the API
                level by `permission:Student_Edit`; Super Admin
                bypasses via CheckPermission. */}
            {(() => {
              const isDropped = normalizeStudentStatus((studentData as any).status) === "dropped";
              return (
                <>
                  <Tooltip title="Student lifecycle actions">
                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={<MoreVertIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => setActionsAnchor(e.currentTarget)}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.12)",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 13,
                        borderRadius: "10px",
                        px: 2,
                        border: "1px solid rgba(255,255,255,0.1)",
                        boxShadow: "none",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.2)", boxShadow: "none" },
                      }}
                    >
                      Actions
                    </Button>
                  </Tooltip>
                  <Menu
                    anchorEl={actionsAnchor}
                    open={!!actionsAnchor}
                    onClose={() => setActionsAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    {!isDropped && (
                      <MenuItem onClick={() => { setActionsAnchor(null); setDropDialogOpen(true); }}>
                        <ListItemIcon><PersonOffOutlinedIcon fontSize="small" sx={{ color: "#ef4444" }} /></ListItemIcon>
                        <ListItemText primary="Drop Student" secondary="Requires date + reason" />
                      </MenuItem>
                    )}
                    {isDropped && (
                      <MenuItem onClick={() => { setActionsAnchor(null); setReactivateDialogOpen(true); }}>
                        <ListItemIcon><HowToRegOutlinedIcon fontSize="small" sx={{ color: "#16a34a" }} /></ListItemIcon>
                        <ListItemText primary="Make Active Again" secondary="Restore to active" />
                      </MenuItem>
                    )}
                  </Menu>
                </>
              );
            })()}
          </Stack>
        </Box>
      </Box>

      {/* ════════════════════════════════════════════
          QUICK STATS STRIP
         ════════════════════════════════════════════ */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STAT_CARDS.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Box
              sx={{
                bgcolor: stat.bg,
                borderRadius: "14px",
                p: { xs: 2, md: 2.5 },
                border: `1px solid ${alpha(stat.color, 0.1)}`,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                cursor: "default",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: `0 8px 24px ${alpha(stat.color, 0.15)}`,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "10px",
                    bgcolor: alpha(stat.color, 0.12),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: stat.color,
                    "& svg": { fontSize: 20 },
                  }}
                >
                  {stat.icon}
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: 28, md: 34 },
                  fontWeight: 800,
                  color: stat.color,
                  lineHeight: 1,
                  letterSpacing: "-0.02em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {statValues[stat.key]}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: alpha(stat.color, 0.65),
                  mt: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* ════════════════════════════════════════════
          TAB NAVIGATION

          Wraps to as many rows as needed — 13 tabs will not fit in one row
          at any laptop width, and hiding half of them behind scroll (with
          or without an affordance) is worse than showing all of them
          across two rows. Left-aligned at every breakpoint; the row grows
          to two rows on desktop, three on narrower viewports.

          Keyboard is not wired here — tabs are non-focusable <Box>s. That
          is a separate accessibility item, tracked outside this fix.
         ════════════════════════════════════════════ */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "14px",
          border: "1px solid rgba(0,0,0,0.06)",
          mb: 3,
          p: 1,
          display: "flex",
          flexWrap: "wrap",
          rowGap: 0.5,
          columnGap: 0.5,
          justifyContent: "flex-start",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        {TAB_CONFIG.map((tab) => {
          const isActive = activeSection === tab.key;
          return (
            <Box
              key={tab.key}
              onClick={() => handleContainerChange(tab.key)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: { xs: 1.5, md: 2 },
                py: 1,
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                flexShrink: 0,
                bgcolor: isActive ? theme.palette.primary.main : "transparent",
                color: isActive ? "#fff" : "text.secondary",
                boxShadow: isActive ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                "&:hover": {
                  bgcolor: isActive
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.06),
                  color: isActive ? "#fff" : theme.palette.primary.main,
                },
              }}
            >
              {React.cloneElement(tab.icon as React.ReactElement, {
                sx: { fontSize: 17, opacity: isActive ? 1 : 0.7 },
              })}
              {!isMobileRes && (
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: isActive ? 600 : 500,
                    whiteSpace: "nowrap",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {tab.label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ════════════════════════════════════════════
          TAB CONTENT
         ════════════════════════════════════════════ */}
      <Box>
        <Suspense
          fallback={
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          }
        >
          {renderContent()}
        </Suspense>
      </Box>

      {/* ════════════════════════════════════════════
          PROFILE IMAGE DIALOG
         ════════════════════════════════════════════ */}
      <Dialog open={openImage} onClose={handleCloseImage} maxWidth="xs" fullWidth>
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1}>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Profile Picture</Typography>
          <Button
            variant="contained"
            className={classes.editButton}
            onClick={handleCloseImage}
            sx={{ minWidth: 36, p: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
        <Divider />
        <DialogContent sx={{ textAlign: "center" }}>
          {selectedEmpImg || selectedImage ? (
            <Box>
              <Avatar
                src={selectedImage ? URL.createObjectURL(selectedImage) : selectedEmpImg}
                sx={{ width: 200, height: 200, mx: "auto", borderRadius: "50%" }}
              />
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="profile-upload"
                type="file"
                onChange={handleImageChange}
              />
              <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" mt={2}>
                <label htmlFor="profile-upload">
                  <Button
                    variant="contained"
                    className={classes.addButton}
                    component="span"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                  >
                    Change Image
                  </Button>
                </label>
                {selectedEmpImg && (
                  <Button
                    variant="outlined"
                    className={classes.addButton}
                    size="small"
                    component="a"
                    href={selectedEmpImg}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<DownloadOutlinedIcon />}
                  >
                    Download
                  </Button>
                )}
                {(selectedEmpImg || selectedImage) && (
                  <Button
                    variant="outlined"
                    className={classes.addButton}
                    color="error"
                    size="small"
                    onClick={() => formikRemoved.submitForm()}
                    disabled={saveIsLoading}
                    startIcon={<DeleteOutlineIcon />}
                  >
                    {saveIsLoading && (
                      <CircularProgress size={18} className={classes.isLoading} sx={{ mr: 1 }} />
                    )}
                    Remove
                  </Button>
                )}
                {saveIsLoading && <BackDrop open={saveIsLoading} />}
              </Box>
            </Box>
          ) : (
            <Box>
              <Avatar sx={{ width: 200, height: 200, mx: "auto", bgcolor: "#f0f0f0" }} />
              <Box mt={2}>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="profile-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <label htmlFor="profile-upload">
                  <Button
                    variant="contained"
                    className={classes.addButton}
                    component="span"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                  >
                    Upload Image
                  </Button>
                </label>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider />
        {selectedImage && (
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button
              variant="contained"
              className={classes.addButton}
              size="small"
              onClick={() => formik.submitForm()}
              disabled={!selectedImage || saveIsLoading}
            >
              {saveIsLoading && (
                <CircularProgress size={18} className={classes.isLoading} sx={{ mr: 1 }} />
              )}
              Update
            </Button>
            {saveIsLoading && <BackDrop open={saveIsLoading} />}
          </DialogActions>
        )}
      </Dialog>

      {coursingToDelete && (
        <DeletePopup
          open={openDeleteCategory}
          handleCloseDelete={handleCloseDeletePopup}
          onConfirm={async () => {
            await handleDelete(coursingToDelete.id.toString());
          }}
          title="Delete Student"
          content={`Are you sure you want to delete "${
            coursingToDelete.first_name +
            " " +
            coursingToDelete.middle_name +
            " " +
            coursingToDelete.last_name
          }"?`}
        />
      )}
      {/* Student lifecycle dialogs. Rendered at the root so they layer
          above the profile hero on both mobile + desktop. */}
      {studentData?.id && (
        <>
          <DropStudentDialog
            open={dropDialogOpen}
            onClose={() => setDropDialogOpen(false)}
            studentId={Number(studentData.id)}
            studentName={`${studentData.first_name ?? ""} ${studentData.last_name ?? ""}`.trim() || "Student"}
          />
          <ReactivateStudentDialog
            open={reactivateDialogOpen}
            onClose={() => setReactivateDialogOpen(false)}
            studentId={Number(studentData.id)}
            studentName={`${studentData.first_name ?? ""} ${studentData.last_name ?? ""}`.trim() || "Student"}
          />
          {canViewStudentNotes && (
            <HealthAlertDialog
              open={healthDialogOpen}
              onClose={() => setHealthDialogOpen(false)}
              studentId={Number(studentData.id)}
              studentName={`${studentData.first_name ?? ""} ${studentData.last_name ?? ""}`.trim() || "Student"}
            />
          )}
          {/* ── Communication timeline (Pass E) ─────────────────
                 Reads /api/students/{id}/communication-history —
                 server-scopes to the student's linked parent
                 addresses. Legacy Mail::send flows (invoices, class
                 reminders, password reset) are deliberately absent
                 because they never write to the emails audit table. */}
          {studentData?.id && (
            <Box sx={{ mt: 3 }}>
              <ProfileTimelineSection profileType="student" profileId={Number(studentData.id)} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * Health & Safety Notes modal. Backed by GET /students/{id}/health-notes
 * which returns raw student_notes rows (noteType='medical'). We render the
 * original note text verbatim — no AI summary, no fabricated allergy/
 * seizure/medication sections (medical storage is free-form).
 */
const HealthAlertDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  studentId: number;
  studentName: string;
}> = ({ open, onClose, studentId, studentName }) => {
  const { data, isFetching, isError } = useGetStudentHealthNotesQuery(
    { studentId },
    { skip: !open || !studentId },
  );
  const payload: any = (data as any)?.data || {};
  const notes: any[] = Array.isArray(payload.notes) ? payload.notes : [];
  const count = Number(payload.count ?? notes.length);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <MedicalServicesOutlinedIcon sx={{ color: "#b45309", fontSize: 28, mt: 0.25 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
              Health & Safety Notes
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
              {studentName}
            </Typography>
          </Box>
          <Button onClick={onClose} size="small" sx={{ minWidth: 0, color: "text.secondary" }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </Button>
        </Box>
        <Divider />
        <Box sx={{ px: 3, pt: 2, pb: 3, maxHeight: "60vh", overflow: "auto" }}>
          {isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          ) : isError ? (
            <Typography sx={{ fontSize: 13, color: "error.main" }}>
              Unable to load health notes.
            </Typography>
          ) : notes.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              No medical notes on file.
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1.5 }}>
                {count === 1 ? "1 medical note on file" : `${count} medical notes on file`}
              </Typography>
              <Stack spacing={1.5}>
                {notes.map((n: any) => {
                  const createdISO = n.created_at || n.createdAt || null;
                  const createdLabel = createdISO
                    ? new Date(createdISO).toLocaleString(undefined, {
                        year: "numeric", month: "short", day: "numeric",
                        hour: "numeric", minute: "2-digit",
                      })
                    : null;
                  const author = n.creator?.username || n.creator?.email || null;
                  return (
                    <Box
                      key={n.id}
                      sx={{
                        border: "1px solid rgba(245,158,11,0.35)",
                        bgcolor: alpha("#fef3c7", 0.5),
                        borderRadius: "10px",
                        p: 1.75,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                        {n.isPinned ? (
                          <PushPinIcon sx={{ fontSize: 14, color: "#b45309" }} />
                        ) : null}
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#78350f", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                          Medical Note
                        </Typography>
                      </Box>
                      {n.title && (
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                          {n.title}
                        </Typography>
                      )}
                      {n.content && (
                        <Typography sx={{ fontSize: 13, color: "text.primary", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>
                          {n.content}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
                        {createdLabel && (
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                            Created: {createdLabel}
                          </Typography>
                        )}
                        {author && (
                          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                            By: {author}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Index;
